import React from "react";

import { useScoped } from "~/tools/impulse";

import { Group, Rect } from "../../_canvas";
import { useTimelineContext } from "../_context";

import {
	type CommonTimelineBucketProps,
	TIMELINE_EVENT_CATEGORY_PROPERTIES,
	type TimelineEventBucket,
	type TimelineEventCategory,
	getTimelineEventBucketEndTime,
	getTimelineEventBucketStartTime,
} from "./domain";
import { TimelineEventBucketsContainer } from "./TimelineEventBucketsContainer";
import { TimelineEventBucketsPopover, type TimelineEventBucketsPopoverOptions } from "./TimelineEventBucketsPopover";

export interface TimelineEventBucketsProps<TBucket> extends CommonTimelineBucketProps<TBucket> {
	eventCategory?: TimelineEventCategory;
	popover?: false | TimelineEventBucketsPopoverOptions<TBucket>;
	isBucketSelected?: (bucket: TBucket) => boolean;
}

export const TimelineEventBuckets = <TBucket extends TimelineEventBucket>({
	eventCategory = "*",
	popover = {},
	buckets,
	isBucketSelected,
	...props
}: TimelineEventBucketsProps<TBucket>): React.ReactElement => (
	<TimelineEventBucketsContainer
		{...props}
		buckets={buckets}
		getBucketEndTime={getTimelineEventBucketEndTime}
		getBucketStartTime={getTimelineEventBucketStartTime}
		renderBefore={(bucketSize) =>
			popover && <TimelineEventBucketsPopover bucketSize={bucketSize} eventCategory={eventCategory} {...popover} />
		}
	>
		{({ bucket, x, width, height }, scaleCount) => (
			<Bucket
				bucket={bucket}
				canHover={popover !== false}
				count={scaleCount(bucket.count)}
				eventCategory={eventCategory}
				height={height}
				isBucketSelected={isBucketSelected}
				width={width}
				x={x}
			/>
		)}
	</TimelineEventBucketsContainer>
);

const Bucket: React.FC<{
	eventCategory: TimelineEventCategory;
	bucket: TimelineEventBucket;
	canHover: boolean;
	count: number;
	x: number;
	width: number;
	height: number;
	isBucketSelected?: (bucket: TimelineEventBucket) => boolean;
}> = ({ eventCategory, canHover, bucket, x, width, height, count, isBucketSelected }) => {
	const { mainColor, hoveredColor, selectedColor = hoveredColor } = TIMELINE_EVENT_CATEGORY_PROPERTIES[eventCategory];
	const bucketStartTime = getTimelineEventBucketStartTime(bucket);
	const { eventBucketsState } = useTimelineContext();
	const isHovered = useScoped(
		(scope) => canHover && eventBucketsState.isBucketHovered(scope, eventCategory, bucketStartTime),
		[eventBucketsState, eventCategory, bucketStartTime, canHover],
	);

	return (
		<Group x={x}>
			<Rect
				fill={isHovered ? hoveredColor : isBucketSelected?.(bucket) ? selectedColor : mainColor}
				height={count}
				listening={false}
				width={width}
				y={height - count}
			/>

			{/* a hit ara for mouse hover effects */}
			<Rect
				height={height}
				onMouseEnter={({ evt: event, target }) => {
					if (event.target instanceof HTMLCanvasElement) {
						const canvasRect = event.target.getBoundingClientRect();
						const { x, y } = target.getAbsolutePosition();

						eventBucketsState.hoveredBucket.setValue({
							eventCategory,
							bucket,
							position: { x: canvasRect.x + x, y: canvasRect.y + y },
						});
					}
				}}
				onMouseLeave={() => {
					eventBucketsState.hoveredBucket.setValue(undefined);
				}}
				width={width}
				y={0}
			/>
		</Group>
	);
};
